import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import Grid from "@mui/material/Grid2";
import { Box, Card, CardActionArea, CardContent, CardMedia, Typography } from "@mui/material";
import DuckFootIcon from "assets/icons/DuckFootIcon";
import { useTranslation } from "react-i18next";

interface IFeaturedArticlesListProps {
  blok: { headline: string; articles: any };
}

const FeaturedArticlesList = ({ blok }: IFeaturedArticlesListProps) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [showIcon1, setShowIcon1] = useState(false);
  const [showIcon2, setShowIcon2] = useState(false);
  const featuredSectionRef = useRef(null);
  const local = i18n.language;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => setShowIcon1(true), 2000);
            setTimeout(() => setShowIcon2(true), 2500);
            observer.unobserve(entry.target);
          } else {
          }
        });
      },
      { threshold: 0.5 }
    );

    if (featuredSectionRef.current) {
      observer.observe(featuredSectionRef.current);
    }

    return () => {
      if (featuredSectionRef.current) {
        observer.unobserve(featuredSectionRef.current);
      }
    };
  }, []);

  return (
    <Box
      component="section"
      ref={featuredSectionRef}
      {...storyblokEditable(blok)}
      sx={{ position: "relative", marginBottom: "100px" }}
    >
      <Typography
        component="h2"
        sx={{
          marginBottom: "24px",
          color: "#2E263D",
          fontSize: "24px",
          fontWeight: 500,
          lineHeight: "32px",
        }}
      >
        {blok.headline}
      </Typography>
      <Grid component="ul" container spacing={2}>
        {blok?.articles?.map((article: any) => {
          const articleTitle = article?.content?.main_title || "";
          const articlePreviewText = article?.content?.preview_text;
          const articlePreviewImage = article?.content?.preview_image?.filename;
          const articlePreviewImageAlt = article?.content?.preview_image?.alt || "";
          const fullSlug = article?.full_slug;
          const articleKey = article?.uuid;

          return (
            <Grid component="li" key={articleKey} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card sx={{ height: "100%", borderRadius: "24px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}>
                <CardActionArea
                  onClick={() => navigate(local === "en" ? `/blog/${fullSlug}` : `/blog/${local}/${fullSlug}`)}
                >
                  <CardMedia component="img" height="220" image={articlePreviewImage} alt={articlePreviewImageAlt} />
                  <CardContent>
                    <Typography
                      gutterBottom
                      component="h3"
                      sx={{ color: "#2E263D", fontSize: "18px", fontWeight: 600, lineHeight: "24px" }}
                    >
                      {articleTitle}
                    </Typography>
                    <Typography component="p" sx={{ color: "#2E263D", fontSize: "16px", lineHeight: "24px" }}>
                      {articlePreviewText}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {showIcon1 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            transform: { xs: "rotate(80deg) translate(104px, -250px)", md: "rotate(80deg) translate(110px, -290px)" },
            opacity: showIcon1 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
      {showIcon2 && (
        <Box
          className="icon-animation"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            transform: { xs: "rotate(31deg) translate(324px, -84px)", md: "rotate(31deg) translate(358px, -100px)" },
            opacity: showIcon2 ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          <DuckFootIcon />
        </Box>
      )}
    </Box>
  );
};

export default FeaturedArticlesList;
